
import { usePollFetch } from './usePollFetch';
import { useUserVotes } from './useUserVotes';
import { usePollNavigation } from './usePollNavigation';
import { usePollVotesRealtime } from './usePollVotesRealtime';
import { useEffect, useCallback, useRef, useMemo } from 'react';
import { Poll } from '@/types/poll';
import { toast } from 'sonner';

export const useWidgetPolls = (countryCode?: string) => {
  const initialLoadDone = useRef(false);
  const refreshInProgress = useRef(false);
  const refreshAttemptTime = useRef(0);
  const lastCountryCode = useRef(countryCode);
  
  const { 
    polls: allPolls, 
    setPolls: setAllPolls, 
    isLoading, 
    error: fetchError,
    refetchPolls
  } = usePollFetch({ 
    countryCode,
    includeAllStatuses: true
  }); 

  // Log poll data for debugging
  useEffect(() => {
    if (allPolls.length > 0) {
      console.log('useWidgetPolls: all polls loaded:', allPolls.length);
      console.log('useWidgetPolls: sample poll data with closes_at:', allPolls.slice(0, 2).map(p => ({
        id: p.id,
        status: p.status,
        closes_at: p.closes_at
      })));
    }
  }, [allPolls]);

  // Enable real-time updates for poll votes
  const { 
    userVotes, 
    setUserVotes,
    refreshVotes
  } = useUserVotes();

  // Pass the userVotes to the usePollVoteRealtime hook
  usePollVotesRealtime(allPolls, setAllPolls, userVotes, setUserVotes);

  const activePolls = useMemo(() => 
    allPolls.filter(poll => poll.status === 'live'),
    [allPolls]
  );

  const closedPolls = useMemo(() => 
    allPolls.filter(poll => poll.status === 'closed'),
    [allPolls]
  );
  
  const {
    currentPollIndex,
    setCurrentPollIndex,
    nextPoll,
    prevPoll
  } = usePollNavigation(activePolls);

  // If the active polls change and the current index is out of bounds, reset it
  useEffect(() => {
    if (activePolls.length > 0 && currentPollIndex >= activePolls.length) {
      setCurrentPollIndex(0);
    }
    
    if (!isLoading) {
      initialLoadDone.current = true;
    }
  }, [activePolls, currentPollIndex, setCurrentPollIndex, isLoading]);

  const handleRefresh = useCallback(() => {
    if (refreshInProgress.current) {
      console.log('Refresh already in progress, skipping');
      return;
    }
    
    const now = Date.now();
    if (now - refreshAttemptTime.current < 1000) {
      console.log('Refresh attempted too frequently, skipping');
      return;
    }
    
    console.log('Performing complete poll widget refresh');
    refreshInProgress.current = true;
    refreshAttemptTime.current = now;
    
    try {
      // Don't reset the current poll index on refresh to maintain position
      refetchPolls();
      refreshVotes();
    } catch (error) {
      console.error('Error during refresh:', error);
      toast.error('Failed to refresh polls. Please try again.');
    } finally {
      setTimeout(() => {
        refreshInProgress.current = false;
      }, 1000);
    }
  }, [refetchPolls, refreshVotes]);

  useEffect(() => {
    if (!initialLoadDone.current && !refreshInProgress.current) {
      console.log('Initial loading of widget polls for country:', countryCode);
      
      if (countryCode !== lastCountryCode.current) {
        console.log(`Country code changed from ${lastCountryCode.current} to ${countryCode}, refreshing polls`);
        lastCountryCode.current = countryCode;
        
        setTimeout(() => {
          if (!refreshInProgress.current) {
            handleRefresh();
          }
        }, 100);
      }
    }
  }, [countryCode, handleRefresh]);

  return {
    polls: activePolls,
    setPolls: setAllPolls,
    currentPollIndex,
    setCurrentPollIndex,
    isLoading,
    fetchError,
    userVotes,
    setUserVotes,
    nextPoll,
    prevPoll,
    refresh: handleRefresh,
    closedPolls
  };
};
